import React, { useState } from 'react';
import { ActivityIndicator, Linking, Pressable, StyleSheet, Text, View, } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigation } from '@react-navigation/native';
import { environmentSelector, signout } from '../redux/appSlice';
import { useGetMeQuery, useRequestAccountDeletionMutation, } from '../services/abbiWayApi';
import { Alert } from './Alert';
import { Button } from './Button';
import Avatar from './Avatar';
import { theme } from '../theme';
import EnvironmentBadge from './EnvironmentBadge';
import { PRIVACY_POLICY_LINK_LABEL } from '../config/storeMessaging';
import { deployedOnLabel } from '../utils/deployedOnLabel';
const ENVIRONMENT_PREFIXES = {
    local: 'local$',
    test: 'test$',
    prod: 'prod$',
};
const openPrivacy = () => {
    const origin = typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : '';
    Linking.openURL(`${origin}/privacy.html`);
};
export const SettingsView = ({ webMenu = false, adminMenu = false, }) => {
    const dispatch = useDispatch();
    const navigation = useNavigation();
    const environment = useSelector(environmentSelector);
    const [requestDeletion, { isLoading: isRequestingDeletion }] = useRequestAccountDeletionMutation();
    const [deletionFeedback, setDeletionFeedback] = useState(null);
    const signoutPress = () => {
        dispatch(signout());
    };
    const { data: me } = useGetMeQuery();
    const environmentPrefix = ENVIRONMENT_PREFIXES[environment] ?? `${String(environment)}$`;
    const handleReportIssue = () => {
        let navPath = [];
        try {
            if (navigation && typeof navigation.getState === 'function') {
                const state = navigation.getState();
                if (state) {
                    const routes = [];
                    const extractRoutes = (navState, depth = 0) => {
                        if (!navState || depth > 10) {
                            return;
                        }
                        if (navState.routes && Array.isArray(navState.routes)) {
                            for (const route of navState.routes) {
                                if (route &&
                                    typeof route === 'object' &&
                                    route.name &&
                                    route.name !== 'IssueReport') {
                                    routes.push(route.name);
                                }
                                if (route && route.state && typeof route.state === 'object') {
                                    extractRoutes(route.state, depth + 1);
                                }
                            }
                        }
                    };
                    extractRoutes(state);
                    navPath = routes;
                }
            }
            if (navPath.length === 0 &&
                navigation &&
                typeof navigation.getCurrentRoute === 'function') {
                const currentRoute = navigation.getCurrentRoute();
                if (currentRoute?.name) {
                    navPath = [currentRoute.name];
                }
            }
        }
        catch {
            // Silently handle navigation path extraction errors
        }
        navigation.navigate('IssueReport', {
            fromScreen: 'Settings',
            navigationPath: navPath,
        });
    };
    const handleDeleteAccountPress = () => {
        Alert('Request account deletion', 'This sends a request to Abbi’s Way. Your account is not deleted automatically. Someone will follow up with you within a few days.', [
            {
                text: 'Request deletion',
                style: 'destructive',
                onPress: () => {
                    requestDeletion()
                        .unwrap()
                        .then(result => {
                        const message = result?.message ||
                            'Deletion request received. We’ll get back to you within a few days.';
                        setDeletionFeedback(message);
                        Alert('Request received', message, [
                            { text: 'OK', style: 'default' },
                        ]);
                    })
                        .catch(() => {
                        Alert('Could not send request', 'Something went wrong sending your deletion request. Please try again, or email support@abbisway.com.', [{ text: 'OK', style: 'default' }]);
                    });
                },
            },
            { text: 'Cancel', style: 'cancel' },
        ]);
    };
    return (<View style={styles.container}>
      {me && (<View style={styles.avatar}>
          <Avatar firstName={me.firstName} lastName={me.lastName}/>
          <Text style={styles.username}>
            {`${me.firstName} ${me.lastName}`}
          </Text>
          {me.email && (<Text style={styles.email}>
              {`${environmentPrefix}${me.email}`}
            </Text>)}
          <EnvironmentBadge />
          {webMenu && me.programType != null ? (<Text style={styles.detail}>{`Program type: ${me.programType}`}</Text>) : null}
          {webMenu && (me.weekStartsOn === 0 || me.weekStartsOn === 1) ? (<Text style={styles.detail}>
              {`Week starts: ${me.weekStartsOn === 0 ? 'Sunday' : 'Monday'}`}
            </Text>) : null}
        </View>)}
      {/* #388: bug reporting is a tester tool; hide it on prod sessions. */}
      {!webMenu && environment !== 'prod' && (<View style={styles.buttonContainer}>
          <Button title="Report an Issue" onPress={handleReportIssue}/>
        </View>)}
      <View style={styles.buttonContainer}>
        <Button title="Signout" onPress={signoutPress}/>
      </View>
      {/* #351: a quiet link at the bottom, well away from Signout. */}
      {adminMenu ? null : (<View style={[styles.deleteArea, webMenu && styles.deleteAreaMenu]}>
          {deletionFeedback ? (<Text style={styles.feedback}>{deletionFeedback}</Text>) : null}
          {isRequestingDeletion ? (<ActivityIndicator color={theme.colors.textCaption}/>) : (<Pressable onPress={handleDeleteAccountPress} accessibilityRole="link" accessibilityLabel="Delete my account" hitSlop={8}>
              <Text style={styles.deleteLink}>Delete my account</Text>
            </Pressable>)}
          {webMenu ? (<>
              <Pressable style={styles.privacyRow} onPress={openPrivacy} accessibilityRole="link" accessibilityLabel={PRIVACY_POLICY_LINK_LABEL} hitSlop={8}>
                <Text style={styles.privacyLink}>
                  {PRIVACY_POLICY_LINK_LABEL}
                </Text>
              </Pressable>
              <Text style={styles.deployed}>{deployedOnLabel(BUILD_DATE)}</Text>
            </>) : null}
        </View>)}
    </View>);
};
const styles = StyleSheet.create({
    container: {
        flex: 1,
        marginTop: 16,
        marginLeft: 16,
        marginRight: 16,
    },
    avatar: {
        justifyContent: 'center',
        alignItems: 'center',
    },
    username: {
        fontSize: 16,
        padding: 5,
    },
    email: {
        fontSize: 14,
        padding: 4,
        color: theme.colors.textCaption,
    },
    buttonContainer: {
        marginTop: 16,
    },
    deleteArea: {
        marginTop: 'auto',
        paddingTop: 32,
        paddingBottom: 32,
        alignItems: 'center',
    },
    deleteLink: {
        fontSize: 15,
        fontWeight: '600',
        color: theme.colors.link,
        textDecorationLine: 'underline',
        textAlign: 'center',
    },
    detail: {
        fontSize: 14,
        paddingTop: 4,
        color: theme.colors.textSecondary,
    },
    deleteAreaMenu: {
        marginTop: 0,
        paddingTop: 24,
        paddingBottom: 16,
    },
    privacyRow: {
        marginTop: 16,
    },
    privacyLink: {
        fontSize: 14,
        color: theme.colors.link,
        textDecorationLine: 'underline',
        textAlign: 'center',
    },
    deployed: {
        marginTop: 12,
        fontSize: 12,
        fontStyle: 'italic',
        color: theme.colors.textCaption,
        textAlign: 'center',
    },
    feedback: {
        marginBottom: 12,
        fontSize: 14,
        color: theme.colors.textCaption,
        textAlign: 'center',
    },
});
